import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { ApolloError } from '@apollo/client/errors'
import {
  useFindInvoiceLazyQuery,
  useFindPartnerByPaymentsPortalUrlLazyQuery,
  CustomerPartnerFragment,
  CustomerFindInvoiceFragment
} from 'graph/generated/payments/graphql-types'
import { useRouter } from 'next/router'
import { createBrandColor } from 'utils/colors'
import { ColorProps } from 'utils/types'
import { origin, paymentsDomainLocalFix } from 'utils/window'

/**
 * Provider that supplies company information when a company url is provided.
 * Supplies invoice information when id is url param
 */

interface ICompanyAndInvoiceContext {
  company: CustomerPartnerFragment
  brandColor: ColorProps
  invoice: CustomerFindInvoiceFragment | null
  error: ApolloError
  loading: boolean
  paymentAmount: number
  setPaymentAmount: (amount: number) => void
  installmentTerm: number
  setInstallmentTerm: (term: number) => void
}

export const CompanyAndInvoiceContext =
  createContext<ICompanyAndInvoiceContext>({
    company: null,
    brandColor: null,
    invoice: null,
    error: null,
    loading: false,
    paymentAmount: 0,
    installmentTerm: 1,
    setPaymentAmount: () => {},
    setInstallmentTerm: () => {}
  })

const CompanyAndInvoiceProvider = ({ children }) => {
  const {
    query: { invoice }
  } = useRouter()
  const [installmentTerm, setInstallmentTerm] = useState<number>(1)
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null)

  const [fetchCompany, companyQuery] =
    useFindPartnerByPaymentsPortalUrlLazyQuery()
  const [fetchInvoice, invoiceQuery] = useFindInvoiceLazyQuery()

  const value = useMemo(() => {
    const company = companyQuery.data?.FindPartnerByPaymentsPortalUrl

    // set only on the first load
    if (!paymentAmount) {
      setPaymentAmount(invoiceQuery.data?.FindInvoice?.amount ?? 0)
    }

    return {
      company,
      brandColor: createBrandColor(company?.paymentsPortalCustomization?.color),
      invoice: invoiceQuery.data?.FindInvoice,
      loading: companyQuery.loading,
      error: companyQuery.error || invoiceQuery.error,
      paymentAmount,
      setPaymentAmount,
      installmentTerm,
      setInstallmentTerm
    }
  }, [
    companyQuery.data,
    invoiceQuery.data,
    companyQuery.loading,
    paymentAmount,
    installmentTerm
  ])

  useEffect(() => {
    fetchCompany({ variables: { url: paymentsDomainLocalFix } })
    if (invoice) {
      fetchInvoice({ variables: { id: invoice as string } })
    }
  }, [invoice, origin])

  return (
    <CompanyAndInvoiceContext.Provider value={value}>
      {children}
    </CompanyAndInvoiceContext.Provider>
  )
}

export const useCompanyAndInvoiceContext = () =>
  useContext(CompanyAndInvoiceContext)

export default CompanyAndInvoiceProvider
